import React, { useState } from "react";
import {
  Box,
  Grid,
  FormControlLabel,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  InputLabel,
  Select,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Field, useFormikContext } from "formik";
import Switch from "@mui/material/Switch";

import Div from "@jumbo/shared/Div";
import { Label } from "app/pages/presentation/module/form/types";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DateTimeField, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { CURRENCY_CODES } from "app/utils/constants/settings";
import { useDsokCustomerDetail } from "app/features/dsokCustomerDetail/data/useDsokCustomerDetail";
import { useDeviceWorkspaceSelectOptions } from "app/pages/customers/dsok/hooks/useDeviceWorkspaceSelectOptions";
import PosUrlGenerateModal from "./PosUrlGenerateModal";

const GeneralSection = ({
  device,
  setFieldValue,
  values,
  t,
  isAdmin,
  isPartnerAdmin,
  isPartner,
  isCustomer = false,
  showConnectionCode = false,
  previewButtonLink,
  callScreenUrl,
  orderStatusScreenUrl,
  isFrontendDemoDevice,
  manuelChecking,
  handleManualCheckDevice,
  /** When set, workspace list comes from POS Workspaces API only (customer-detail Devices page). */
  posWorkspacesCustomerId,
  /** Edited customer's BE domain for correct prod/staging POS workspace API routing. */
  posWorkspacesBeDomain,
}) => {
  const { errors } = useFormikContext();
  const [posUrlModalOpen, setPosUrlModalOpen] = useState(false);
  const {
    dsokSettings,
    customer: detailCustomer,
    loading: customerDetailLoading,
    customerUrl: detailCustomerUrl,
  } = useDsokCustomerDetail();
  /** Default DsokCustomerDetailDataContext has customerUrl: null; avoid treating as “loading” outside that layout. */
  const posModalCustomerLoading =
    detailCustomerUrl != null ? customerDetailLoading : false;
  const beDomain = values?.customer.beDomain;

  const { workspacesData, showWorkspaceRowLoading } =
    useDeviceWorkspaceSelectOptions({
      posWorkspacesCustomerId,
      posWorkspacesBeDomain,
      customerDetailLoading,
      dsokSettings,
      detailCustomer,
      formCustomerWorkspaces: values?.customer?.workspaces,
    });

  const deviceType = values.customer.devices[device].deviceType ?? "kiosk";
  /** Kiosk preview & device check apply to kiosk only; POS uses separate POS URL flow. */
  const isPosDevice = String(deviceType).toLowerCase() === "pos";
  /** Virtual device for external integrations (e.g. AudioEcho) — no DLA, no kiosk UI. */
  const isExternalDevice = String(deviceType).toLowerCase() === "external";
  /** Kiosk-only UI (frontend preview, manual check, connection code) — excludes POS and External. */
  const isKioskDevice = !isPosDevice && !isExternalDevice;

  // WooCommerce je viazané na zariadenie: source=woocommerce → webhook secret + URL (s deviceUuid).
  const externalSourceVal = String(
    values.customer.devices[device]?.externalOrders?.source ?? "",
  ).toLowerCase();
  const isWooExternalDevice = isExternalDevice && externalSourceVal === "woocommerce";
  const wooDeviceWebhookUrl = (() => {
    if (!isWooExternalDevice) return "";
    const beDomain =
      values?.customer?.beDomain ??
      values?.customer?.dsokSettings?.customer?.beDomain ??
      "";
    const custCode =
      values?.customer?.code ??
      values?.customer?.dsokSettings?.customer?.code ??
      "";
    const uuid = values.customer.devices[device]?.uuid || "";
    const safeBe = String(beDomain).replace(/\/+$/, "");
    if (!safeBe || !custCode || !uuid) return "";
    return `${safeBe}/api/v1/external/woocommerce/${custCode}/${uuid}`;
  })();
  const workspaceIdRaw = String(
    values.customer.devices[device].workspaceId ?? "",
  ).trim();
  const customerId = values?.customer?.customerId;
  const deviceUuid = values?.customer?.devices?.[device]?.uuid;

  return (
    <>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.disableMaintenanceCheck`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.disableMaintenanceCheck`,
                    e.target.checked,
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.disableMaintenanceCheck")}
                helpText={t("pages.dsokDetails.disableMaintenanceCheckHelp")}
                sx={{ color: "text.primary" }}
                required={false}
              />
            }
            checked={values.customer.devices[device].disableMaintenanceCheck}
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.forceMaintenance`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.forceMaintenance`,
                    e.target.checked,
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.forceMaintenance")}
                helpText={t("pages.dsokDetails.forceMaintenanceDeviceHelp")}
                sx={{ color: "text.primary" }}
                required={false}
              />
            }
            checked={values.customer.devices[device].forceMaintenance}
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.maintenance`}
                component={Switch}
                disabled
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.maintenance`,
                    e.target.checked,
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.enableMaintenance")}
                helpText={t("pages.dsokDetails.enableMaintenanceDeviceHelp")}
                sx={{ color: "text.primary" }}
                required={false}
              />
            }
            checked={values.customer.devices[device].maintenance}
          />
        </Div>
      </Grid>
      {isAdmin && (
        <>
          <Grid item xs={12} md={6} lg={3}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              {/* frontend demo device */}
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.demoMode`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.demoMode`,
                        e.target.checked,
                      )
                    }
                  />
                }
                label={
                  <Label
                    text={t("pages.dsokDetails.demoMode")}
                    color="primary"
                    helpText={t("pages.dsokDetails.demoModeHelp")}
                  />
                }
                checked={values.customer.devices[device].demoMode}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              {/* frontend demo device */}
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.testMode`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.testMode`,
                        e.target.checked,
                      )
                    }
                  />
                }
                label={
                  <Label
                    text={t("pages.dsokDetails.testMode")}
                    color="primary"
                    helpText={t("pages.dsokDetails.testModeHelp")}
                  />
                }
                checked={values.customer.devices[device].testMode}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.presentationMode`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.presentationMode`,
                        e.target.checked,
                      )
                    }
                  />
                }
                label={
                  <Label
                    text={t("pages.dsokDetails.presentationMode")}
                    color="warning"
                    helpText={t("pages.dsokDetails.presentationModeHelp")}
                  />
                }
                checked={values.customer.devices[device].presentationMode}
              />
            </Div>
          </Grid>
        </>
      )}
      <Grid item xs={12} md={12} lg={12}></Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <JumboTextField
            required
            fullWidth
            InputProps={{
              readOnly: isAdmin ? false : true,
            }}
            InputLabelProps={{ required: false }}
            name={`customer.devices.${device}.deviceName`}
            label={<Label text={t("pages.dsokDetails.deviceName")} required />}
          ></JumboTextField>
        </Div>
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <JumboTextField
            required
            fullWidth
            InputLabelProps={{ required: false }}
            InputProps={{
              readOnly: isAdmin ? false : true,
            }}
            name={`customer.devices.${device}.serialNumber`}
            label={
              <Label text={t("pages.dsokDetails.serialNumber")} required />
            }
          ></JumboTextField>
        </Div>
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimeField
              label={t("pages.dsokDetails.lastHeartbeat")}
              name={`customer.devices.${device}.lastHeartbeat`}
              readOnly
              slotProps={{
                textField: {
                  InputLabelProps: { shrink: true },
                  placeholder: "",
                },
              }}
              value={
                values.customer.devices[device].lastHeartbeat &&
                parseISO(values.customer.devices[device].lastHeartbeat)
              }
            />
          </LocalizationProvider>
        </Div>
      </Grid>
      {/* // toto je tu kvoli prepojeniu na ssh, aby sa vedelo komunikovat na spravnu domenu, je to duplikacia, ale neda sa to momentalne vyhodit */}
      {isAdmin && (
        <Grid item xs={12} md={3} lg={3}>
          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
            <JumboTextField
              required
              fullWidth
              InputLabelProps={{ required: false }}
              InputProps={{
                readOnly: isAdmin ? false : true,
              }}
              name={`customer.devices.${device}.beDomain`}
              label={<Label text={t("pages.dsokDetails.beDomain")} required />}
              value={beDomain}
            ></JumboTextField>
          </Div>
        </Grid>
      )}
      {/* <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <JumboTextField
            required
            select
            InputLabelProps={{ required: false }}
            name={`customer.devices.${device}.beDomain`}
            label={<Label text={t("pages.dsokDetails.beDomain")} required />}
          >
            {BE_DOMAINS.map((domain) => {
              return (
                <MenuItem key={domain} value={domain}>
                  {domain}
                </MenuItem>
              );
            })}
          </JumboTextField>
        </Div>
      </Grid> */}
      <Grid item xs={12} md={12} lg={12}></Grid>
      {isAdmin && (
        <>
          <Grid item xs={12} md={2} lg={1}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                // required
                select
                // InputLabelProps={{ required: true }}
                name={`customer.devices.${device}.currency`}
                label={
                  <Label text={t("pages.dsokDetails.currency")} required />
                }
              >
                {CURRENCY_CODES.map(({ code, name }) => {
                  return (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} lg={3}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                required
                select
                InputLabelProps={{ required: false }}
                name={`customer.devices.${device}.location`}
                label={
                  <Label text={t("pages.dsokDetails.location")} required />
                }
              >
                {Object.keys(values?.customer.locations || {}).map(
                  (location) => {
                    return (
                      <MenuItem key={location} value={location}>
                        {values?.customer.locations[location].name}
                      </MenuItem>
                    );
                  },
                )}
              </JumboTextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5} lg={4}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                }}
                name={`customer.devices.${device}.uuid`}
                label={t("pages.dsokDetails.uuid")}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={5} lg={3}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                name={`customer.devices.${device}.externalId`}
                label={
                  <Label
                    text={t("pages.dsokDetails.externalId")}
                    color="primary"
                    helpText={t("pages.dsokDetails.externalIdHelp")}
                  />
                }
              />
            </Div>
          </Grid>
          {showConnectionCode && (
            <>
              <Grid item xs={12} md={5} lg={4}>
                <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                  <InputLabel id={`device-type-label-${device}`}>
                    {t("pages.dsokDetails.deviceType")}
                  </InputLabel>
                  <Select
                    labelId={`device-type-label-${device}`}
                    label={t("pages.dsokDetails.deviceType")}
                    value={deviceType}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.deviceType`,
                        e.target.value,
                      )
                    }
                  >
                    <MenuItem value="kiosk">
                      {t("pages.dsokDetails.deviceTypeKiosk")}
                    </MenuItem>
                    <MenuItem value="pos">
                      {t("pages.dsokDetails.deviceTypePos")}
                    </MenuItem>
                    <MenuItem value="external">
                      {t("pages.dsokDetails.deviceTypeExternal")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* External device: source (partner channel). Zobrazí sa pod typom zariadenia. */}
              {isExternalDevice && (
                <Grid item xs={12} md={5} lg={4}>
                  <FormControl
                    fullWidth
                    required
                    error={
                      !values.customer.devices[device]?.externalOrders?.source
                    }
                    sx={{ mt: 1, mb: 1 }}
                  >
                    <InputLabel id={`external-source-label-${device}`}>
                      {t("pages.dsokDetails.externalDevice.source", {
                        defaultValue: "Zdroj",
                      })}
                    </InputLabel>
                    <Select
                      labelId={`external-source-label-${device}`}
                      label={t("pages.dsokDetails.externalDevice.source", {
                        defaultValue: "Zdroj",
                      })}
                      value={
                        values.customer.devices[device]?.externalOrders
                          ?.source ?? ""
                      }
                      onChange={(e) =>
                        setFieldValue(
                          `customer.devices.${device}.externalOrders.source`,
                          e.target.value,
                        )
                      }
                    >
                      <MenuItem value="audioecho">AudioEcho</MenuItem>
                      <MenuItem value="woocommerce">WooCommerce</MenuItem>
                      <MenuItem value="singlePayment">SinglePayment</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {/* WooCommerce je viazané na zariadenie: secret + readonly webhook URL. */}
              {isWooExternalDevice && (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          fullWidth
                          type="password"
                          name={`customer.devices.${device}.externalOrders.webhookSecret`}
                          label={t(
                            "pages.dsokDetails.externalDevice.webhookSecret",
                            { defaultValue: "Webhook secret" },
                          )}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          {t("pages.dsokDetails.externalDevice.webhookUrl", {
                            defaultValue: "Webhook URL (vložiť do WooCommerce)",
                          })}
                        </Typography>
                        {wooDeviceWebhookUrl ? (
                          <>
                            <Box
                              sx={{
                                p: 1,
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                bgcolor: "action.hover",
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                wordBreak: "break-all",
                              }}
                            >
                              {wooDeviceWebhookUrl}
                            </Box>
                            <Button
                              size="small"
                              sx={{ mt: 1 }}
                              onClick={() => {
                                if (navigator?.clipboard?.writeText) {
                                  navigator.clipboard.writeText(
                                    wooDeviceWebhookUrl,
                                  );
                                }
                              }}
                            >
                              {t("pages.dsokDetails.externalDevice.copyUrl", {
                                defaultValue: "Kopírovať URL",
                              })}
                            </Button>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("pages.dsokDetails.externalDevice.webhookUrlHint", {
                              defaultValue:
                                "URL sa vygeneruje po uložení (kód zákazníka + UUID zariadenia).",
                            })}
                          </Typography>
                        )}
                      </Div>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Field
                            name={`customer.devices.${device}.externalOrders.extraProductOptions`}
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                `customer.devices.${device}.externalOrders.extraProductOptions`,
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={
                          <Label
                            text={t(
                              "pages.dsokDetails.externalDevice.extraProductOptions",
                              { defaultValue: "Extra Product Options (prílohy)" },
                            )}
                            helpText={t(
                              "pages.dsokDetails.externalDevice.extraProductOptionsHelp",
                              {
                                defaultValue:
                                  "Pre WooCommerce plugin Extra Product Options (TM EPO): prílohy sa z objednávky prenesú a spárujú podľa názvu s produktmi v Datavision — nájdené sa pridajú ako samostatné položky za cenu z webu, nenájdené sa vynechajú a do poznámky za adresou sa pridá „SKONTROLUJ OBJEDNÁVKU“.",
                              },
                            )}
                          />
                        }
                        checked={
                          values.customer.devices[device]?.externalOrders
                            ?.extraProductOptions === true
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {/* Connection code: kiosk and external devices (not POS). External devices
                  use it the same way (e.g. OBERON sync / downstream run via the DLA). */}
              {(isKioskDevice || isExternalDevice) && (
                <Grid item xs={12} md={5} lg={4}>
                  <Div sx={{ mt: 1, mb: 1 }}>
                    <JumboTextField
                      fullWidth
                      required
                      InputLabelProps={{ required: false }}
                      name={`customer.devices.${device}.connectionCode`}
                      label={
                        <Label
                          text={t("pages.dsokDetails.connectionCode")}
                          color="primary"
                          helpText={t("pages.dsokDetails.connectionCodeHelp")}
                          required
                        />
                      }
                    />
                  </Div>
                </Grid>
              )}
            </>
          )}
          {!isExternalDevice && (
          <Grid item xs={12} md={5} lg={4}>
            {showWorkspaceRowLoading ? (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  {t("pages.dsokDetails.workspaceIdLabel")}
                </Typography>
              </Box>
            ) : workspacesData.length === 0 ? (
              <Div sx={{ mt: 1, mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {t("pages.dsokDetails.workspaceIdLabel")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ wordBreak: "break-all" }}
                  component="div"
                >
                  {workspaceIdRaw ||
                    t("dsokCustomerDetail.devices.workspaceIdNotAssigned", {
                      defaultValue: "—",
                    })}
                </Typography>
                <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                  {t("dsokCustomerDetail.devices.workspaceIdReadOnlyHelp", {
                    defaultValue:
                      "Zoznam pracovných priestorov nie je k dispozícii. Zobrazená hodnota pochádza z uložených údajov zariadenia.",
                  })}
                </FormHelperText>
              </Div>
            ) : (
              <FormControl
                fullWidth
                required={isPosDevice}
                error={Boolean(
                  errors?.customer?.devices?.[device]?.workspaceId,
                )}
                sx={{ mt: 1, mb: 1 }}
              >
                <InputLabel
                  id={`workspace-id-select-label-${device}`}
                  shrink
                >
                  {t("pages.dsokDetails.workspaceIdLabel")}
                </InputLabel>
                <Select
                  labelId={`workspace-id-select-label-${device}`}
                  label={t("pages.dsokDetails.workspaceIdLabel")}
                  notched
                  displayEmpty
                  value={workspaceIdRaw}
                  onChange={(e) =>
                    setFieldValue(
                      `customer.devices.${device}.workspaceId`,
                      e.target.value,
                    )
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <em>
                          {t("pages.dsokDetails.workspaceIdSelectPlaceholder")}
                        </em>
                      );
                    }
                    const w = workspacesData.find((x) => x.uuid === selected);
                    return w?.name ?? selected;
                  }}
                >
                  <MenuItem value="">
                    <em>
                      {t("pages.dsokDetails.workspaceIdSelectPlaceholder")}
                    </em>
                  </MenuItem>
                  {workspaceIdRaw &&
                    !workspacesData.some((w) => w.uuid === workspaceIdRaw) && (
                      <MenuItem value={workspaceIdRaw}>
                        {workspaceIdRaw}
                      </MenuItem>
                    )}
                  {workspacesData.map((w) => (
                    <MenuItem key={w.uuid} value={w.uuid}>
                      {w.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors?.customer?.devices?.[device]?.workspaceId ||
                    t("pages.dsokDetails.workspaceIdSelectHelp")}
                </FormHelperText>
              </FormControl>
            )}
          </Grid>
          )}
        </>
      )}
      <Grid item xs={12} md={12} lg={12}></Grid>
      {(isAdmin || isPartnerAdmin) && isKioskDevice && (
        <>
          <Grid item xs={12} md={4} lg={4}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <Link
                to={previewButtonLink}
                style={{
                  textDecoration: "none",
                  alignSelf: "center",
                }}
                target="_blank"
              >
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  size="small"
                >
                  {t("pages.dsokDetails.openFrontend")}
                </Button>
              </Link>
            </Div>
          </Grid>
        </>
      )}
      {/* Demo devices: show "Kiosk náhľad" for Business Partner, Service Partner, Customer (same preview link as admin) */}
      {isFrontendDemoDevice && (isPartner || isCustomer) && isKioskDevice && (
        <Grid item xs={12} md={4} lg={4}>
          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
            <Link
              to={previewButtonLink}
              style={{
                textDecoration: "none",
                alignSelf: "center",
              }}
              target="_blank"
            >
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                sx={{
                  boxShadow: "none",
                  height: "38px",
                }}
                size="small"
              >
                {t("pages.dsokDetails.kioskPreview")}
              </Button>
            </Link>
          </Div>
        </Grid>
      )}
      {(isAdmin || isPartnerAdmin) && callScreenUrl !== null && (
        <>
          <Grid item xs={12} md={4} lg={4}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <Link
                to={callScreenUrl}
                style={{
                  textDecoration: "none",
                  alignSelf: "center",
                }}
                target="_blank"
              >
                <Button
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  size="small"
                >
                  {t("pages.dsokDetails.callScreenOpen")}
                </Button>
              </Link>
            </Div>
          </Grid>
        </>
      )}
      {(isAdmin || isPartnerAdmin) &&
        !isFrontendDemoDevice &&
        (isKioskDevice || (showConnectionCode && isPosDevice)) && (
        <Grid item xs={12} md={8} lg={8}>
          <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {isKioskDevice && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  disabled={manuelChecking}
                  onClick={() => handleManualCheckDevice(device)}
                >
                  {t("pages.dsokDetails.manuelCheck")}
                </Button>
              )}
              {showConnectionCode && isPosDevice && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    boxShadow: "none",
                    height: "38px",
                  }}
                  onClick={() => setPosUrlModalOpen(true)}
                >
                  {t("pages.dsokDetails.generatePosUrl")}
                </Button>
              )}
            </Stack>
          </Div>
        </Grid>
      )}
      <PosUrlGenerateModal
        open={posUrlModalOpen}
        onClose={() => setPosUrlModalOpen(false)}
        workspaces={workspacesData}
        customerId={customerId}
        deviceUuid={deviceUuid}
        customerLoading={posModalCustomerLoading}
      />
      {/* {isPartner && (
      <>
        <Grid item xs={12} md={4} lg={2}>
          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
            <JumboTextField
              InputProps={{
                readOnly: true,
              }}
              fullWidth
              InputLabelProps={{ required: false }}
              name={`customer.devices.${device}.deviceName`}
              label={
                <Label text={t("pages.dsokDetails.deviceName")} required />
              }
            ></JumboTextField>
          </FormControl>
        </Grid>
      </>
    )} */}
      <Grid item xs={12} md={12} lg={12}></Grid>
      {isAdmin && (
        <>
          <Grid item xs={12} md={4} lg={4}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.orderProcess.enablePrintReceipt`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.orderProcess.enablePrintReceipt`,
                        e.target.checked,
                      )
                    }
                  />
                }
                label={
                  <Label
                    text={t("pages.dsokDetails.enablePrintReceipt")}
                    color="primary"
                    helpText={t("pages.dsokDetails.enablePrintReceiptHelp")}
                  />
                }
                checked={
                  values.customer.devices[device].orderProcess
                    ?.enablePrintReceipt
                }
              />
            </Div>
          </Grid>
          {values?.customer?.communicationType !== "DLA" && (
          <Grid item xs={12} md={3} lg={2}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.restartApp`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.restartApp`,
                        e.target.checked,
                      )
                    }
                  />
                }
                label={
                  <Label
                    text={t("pages.dsokDetails.restartApp")}
                    helpText={t("pages.dsokDetails.restartAppHelp")}
                    sx={{ color: "text.primary" }}
                    required={false}
                  />
                }
                checked={values.customer.devices[device].restartApp}
              />
            </Div>
          </Grid>
          )}
        </>
      )}
    </>
  );
};

export default GeneralSection;
